/**
 * Flow Optimizer — Blocks body migration
 * For Media Devoted | Created By Ciro Autiero
 *
 * Adapts persisted FormulaBlocksBody documents from the pre-subformula shape
 * (`{ ast: ... }`) to the current subformulas-aware shape
 * (`{ subformulas, master, masterExpression }`). Pure, idempotent: safe to run
 * on every read in both backend and frontend.
 */

import { parseExpressionToBlock, serializeBlock } from "./block-node";
import type {
  FormulaBlockNode,
  FormulaBlocksBody,
  FormulaSubformula,
} from "../voluum-flow-optimizer.types";
import { makeSubformulaRefName } from "../voluum-flow-optimizer.types";

interface LegacyBody {
  ast?: FormulaBlockNode | null;
  subformulas?: FormulaSubformula[];
  master?: FormulaBlockNode | null;
  masterExpression?: string;
}

/** Returns a normalized blocks body. Never mutates the input. */
export function migrateBlocksBody(
  body: LegacyBody | null | undefined,
  fallbackName = "Subformula 1",
): FormulaBlocksBody {
  if (body && Array.isArray(body.subformulas)) {
    if (body.subformulas.length === 0 && body.ast) {
      return migrateLegacyAstOnly(body.ast, fallbackName);
    }
    return {
      subformulas: body.subformulas.filter(isRecordSubformula).map(normalizeSubformula),
      master: body.master ?? null,
      masterExpression: body.masterExpression ?? serializeBlock(body.master ?? null),
    };
  }
  const legacyAst = body?.ast ?? null;
  if (!legacyAst) {
    return { subformulas: [], master: null, masterExpression: "" };
  }
  return migrateLegacyAstOnly(legacyAst, fallbackName);
}

function migrateLegacyAstOnly(ast: FormulaBlockNode, fallbackName: string): FormulaBlocksBody {
  const sub = makeSubformulaFromAst(ast, fallbackName);
  const master: FormulaBlockNode = { kind: "subref", subId: sub.id };
  return {
    subformulas: [sub],
    master,
    masterExpression: makeSubformulaRefName(sub.id),
  };
}

/**
 * Runtime-safe shape for React: repairs partial / legacy JSON from API or drafts
 * so `subformulas` is always an array (never undefined).
 */
export function ensureFormulaBlocksBody(
  body: unknown,
  fallbackName = "Subformula 1",
): FormulaBlocksBody {
  if (body == null || typeof body !== "object") {
    return { subformulas: [], master: null, masterExpression: "" };
  }
  return migrateBlocksBody(body as LegacyBody, fallbackName);
}

function isRecordSubformula(s: unknown): s is FormulaSubformula {
  return (
    s != null &&
    typeof s === "object" &&
    typeof (s as FormulaSubformula).id === "string"
  );
}

function normalizeSubformula(sub: FormulaSubformula): FormulaSubformula {
  const name = typeof sub.name === "string" ? sub.name : "Subformula";
  return {
    id: sub.id,
    name,
    description: typeof sub.description === "string" ? sub.description : undefined,
    ast: sub.ast ?? null,
    expression: sub.expression || serializeBlock(sub.ast ?? null),
    ...(typeof sub.weight === "number" ? { weight: sub.weight } : {}),
  };
}

function makeSubformulaFromAst(ast: FormulaBlockNode, name: string): FormulaSubformula {
  return {
    id: generateSubId(),
    name,
    description: undefined,
    ast,
    expression: serializeBlock(ast),
  };
}

let counter = 0;
function generateSubId(): string {
  return `sf_${Date.now().toString(36)}_${counter++}`;
}

/** Re-parses a serialized expression into a master AST. Returns null on failure. */
export function masterExpressionToAst(expression: string): FormulaBlockNode | null {
  return parseExpressionToBlock(expression);
}
